import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import axios from 'axios';
import { ethers } from 'ethers';

// This page only ever needs to call back to the host that served it -- there
// is no legitimate case where /pay should hit a different API host -- so it
// deliberately does not use the app's configurable SERVER_NAME constant (which
// depends on a REACT_APP_SERVER_NAME build-time env var some deploys omit).
const API_BASE = '/api';

const ERC20_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)',
];

// Used only if a wallet doesn't already recognize the target chain (wallet_switchEthereumChain
// throws code 4902). Ethereum mainnet is known to every wallet by default, so in practice this
// only ever fires for Base -- kept per-chain rather than hardcoded so it stays correct if a
// future payment method targets a chain the user's wallet hasn't seen before.
const CHAIN_ADD_PARAMS = {
  base: {
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://mainnet.base.org'],
    blockExplorerUrls: ['https://basescan.org'],
  },
  ethereum: {
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://ethereum.publicnode.com'],
    blockExplorerUrls: ['https://etherscan.io'],
  },
};

const useStyles = makeStyles(() => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    backgroundColor: 'rgba(5,8,20,0.98)',
    minHeight: '100vh',
    padding: 24,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.22), rgba(10,14,30,0.98))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    padding: 24,
    boxSizing: 'border-box',
  },
  title: {
    fontSize: 20,
    fontWeight: 900,
    marginBottom: 12,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 8,
    fontSize: 14,
    color: 'rgba(255,255,255,0.85)',
  },
  status: {
    marginTop: 16,
    fontSize: 14,
    color: 'rgba(255,255,255,0.7)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  error: {
    marginTop: 16,
    fontSize: 14,
    color: '#ff6b6b',
  },
  debug: {
    marginTop: 16,
    padding: 12,
    fontSize: 11,
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.6)',
    background: 'rgba(0,0,0,0.4)',
    borderRadius: 8,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    userSelect: 'text',
  },
  button: {
    marginTop: 20,
    width: '100%',
  },
  sectionTitle: {
    marginTop: 20,
    marginBottom: 8,
    fontSize: 14,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.9)',
  },
  optionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 12,
  },
  packButton: {
    width: '100%',
    justifyContent: 'space-between',
    textAlign: 'left',
    padding: '12px 16px',
  },
  successBox: {
    marginTop: 16,
    padding: 12,
    fontSize: 13,
    lineHeight: 1.6,
    color: '#d9f7e8',
    background: 'rgba(17, 89, 52, 0.35)',
    border: '1px solid rgba(85, 194, 124, 0.45)',
    borderRadius: 8,
    wordBreak: 'break-word',
  },
}));

function formatProductName(product) {
  const value = String(product || '').trim();
  if (!value) return 'e3d.ai payment';
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatPackPrice(pack) {
  const cents = Number(pack && pack.amountUsdCents);
  if (Number.isFinite(cents)) return `$${(cents / 100).toFixed(2)}`;
  const amount = Number(pack && pack.amountUsd);
  if (Number.isFinite(amount)) return `$${amount.toFixed(2)}`;
  return '$0.00';
}

export default function PayPage() {
  const classes = useStyles();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const sessionId = query.get('session');
  const stripeSessionId = query.get('stripe_session_id');
  const stripeCancelled = query.get('stripe_cancelled');

  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('loading');
  const [debugInfo, setDebugInfo] = useState(null);
  const [stripePacks, setStripePacks] = useState([]);
  const [stripeAvailable, setStripeAvailable] = useState(false);
  const [creditKey, setCreditKey] = useState('');
  const [completionMessage, setCompletionMessage] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setError('Missing "session" parameter in the URL.');
      setStatus('error');
      return undefined;
    }

    let cancelled = false;
    const requestUrl = `${API_BASE}/payments/credits/session/${encodeURIComponent(sessionId)}`;
    axios.get(requestUrl)
      .then((res) => {
        if (cancelled) return;
        setSession(res.data);
        setStatus(stripeSessionId ? 'stripe-checking' : 'ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || 'This payment link is invalid or has expired.');
        setDebugInfo({
          requestUrl,
          httpStatus: err?.response?.status ?? null,
          responseBody: err?.response?.data ?? null,
          errorMessage: err?.message ?? null,
          errorCode: err?.code ?? null,
          isNetworkError: !err?.response,
        });
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, stripeSessionId]);

  useEffect(() => {
    if (!session || !session.product) return undefined;

    let cancelled = false;
    axios.get(`${API_BASE}/payments/stripe/packs`, {
      params: { product: session.product },
    })
      .then((res) => {
        if (cancelled) return;
        const packs = Array.isArray(res?.data?.packs) ? res.data.packs : [];
        const enabled = Boolean(res?.data?.enabled) && packs.length > 0;
        setStripePacks(packs);
        setStripeAvailable(enabled);
      })
      .catch(() => {
        if (cancelled) return;
        setStripePacks([]);
        setStripeAvailable(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    if (!session || !stripeSessionId) return undefined;

    let cancelled = false;
    let timerId = null;

    const pollResult = async () => {
      try {
        const res = await axios.get(`${API_BASE}/payments/stripe/session/${encodeURIComponent(stripeSessionId)}/result`);
        if (cancelled) return;
        setCreditKey(res?.data?.creditKey || '');
        setCompletionMessage(
          res?.data?.merged
            ? 'Card payment received. Your credits were added to your existing key.'
            : 'Card payment received.',
        );
        setError(null);
        setDebugInfo(null);
        setStatus('done');
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 202) {
          timerId = window.setTimeout(pollResult, 1500);
          return;
        }
        if (err?.response?.status === 409) {
          setCompletionMessage('Card payment already processed for this session.');
          setCreditKey('');
          setError(err?.response?.data?.error || null);
          setStatus('done');
          return;
        }
        setError(err?.response?.data?.error || 'Unable to confirm your card payment yet.');
        setDebugInfo({
          requestUrl: `${API_BASE}/payments/stripe/session/${encodeURIComponent(stripeSessionId)}/result`,
          httpStatus: err?.response?.status ?? null,
          responseBody: err?.response?.data ?? null,
          errorMessage: err?.message ?? null,
          errorCode: err?.code ?? null,
          isNetworkError: !err?.response,
        });
        setStatus('error');
      }
    };

    pollResult();

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [session, stripeSessionId]);

  const handlePay = useCallback(async () => {
    if (!session) return;
    if (!window.ethereum) {
      setError('No wallet extension detected. Install MetaMask (or a compatible wallet) and reload this page.');
      setStatus('error');
      return;
    }

    try {
      setError(null);
      setStatus('connecting');

      // "any" tells ethers not to lock onto the network detected at
      // construction time -- without it, switching MetaMask to Base a few
      // lines below throws NETWORK_ERROR ("underlying network changed") on
      // the next provider call, since ethers v5 otherwise treats a changed
      // network as fatal.
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const [account] = await provider.send('eth_requestAccounts', []);

      if (String(account).toLowerCase() !== String(session.wallet).toLowerCase()) {
        setError(`This payment is for wallet ${session.wallet}, but your connected wallet is ${account}. Switch accounts in your wallet extension and reload this page.`);
        setStatus('error');
        return;
      }

      const targetChainId = Number(session.quote.payment.chainId);
      const targetChainIdHex = `0x${targetChainId.toString(16)}`;
      const network = await provider.getNetwork();

      if (Number(network.chainId) !== targetChainId) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: targetChainIdHex }],
          });
        } catch (switchError) {
          if (switchError && switchError.code === 4902) {
            const addParams = CHAIN_ADD_PARAMS[session.quote.payment.chain] || CHAIN_ADD_PARAMS.base;
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: targetChainIdHex,
                chainName: session.quote.payment.network || session.quote.payment.chain,
                ...addParams,
              }],
            });
          } else {
            throw switchError;
          }
        }
      }

      setStatus('paying');
      const signer = provider.getSigner();
      const tokenContract = new ethers.Contract(session.quote.payment.tokenAddress, ERC20_ABI, signer);

      let decimals = 18;
      try {
        decimals = await tokenContract.decimals();
      } catch (decimalsError) {
        // Fall back to the ERC20-standard 18 decimals if the read fails.
      }

      const amount = ethers.utils.parseUnits(session.quote.payment.requiredAmount, decimals);
      const tx = await tokenContract.transfer(session.quote.payment.treasuryAddress, amount);

      setStatus('waiting-confirmation');
      await tx.wait();

      setStatus('reporting');
      await axios.post(`${API_BASE}/payments/credits/session/${encodeURIComponent(sessionId)}/complete`, {
        txHash: tx.hash,
      });

      setStatus('done');
    } catch (err) {
      setError(err?.response?.data?.error || err?.reason || err?.message || 'Payment failed. Please try again.');
      setDebugInfo({
        httpStatus: err?.response?.status ?? null,
        responseBody: err?.response?.data ?? null,
        errorMessage: err?.message ?? null,
        errorCode: err?.code ?? null,
        isNetworkError: err && !err.response && !err.reason,
      });
      setStatus('error');
    }
  }, [session, sessionId]);

  const handleCardCheckout = useCallback(async (packId) => {
    if (!session || !packId) return;

    try {
      setError(null);
      setDebugInfo(null);
      setStatus('stripe-redirecting');
      const origin = window.location.origin || 'https://e3d.ai';
      const checkout = await axios.post(`${API_BASE}/payments/stripe/checkout`, {
        product: session.product,
        packId,
        wallet: session.wallet,
        successUrl: `${origin}/pay?session=${encodeURIComponent(sessionId)}&stripe_session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/pay?session=${encodeURIComponent(sessionId)}&stripe_cancelled=1`,
      });
      if (!checkout?.data?.url) {
        throw new Error('Stripe checkout did not return a redirect URL');
      }
      window.location.assign(checkout.data.url);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Unable to start card checkout.');
      setDebugInfo({
        httpStatus: err?.response?.status ?? null,
        responseBody: err?.response?.data ?? null,
        errorMessage: err?.message ?? null,
        errorCode: err?.code ?? null,
        isNetworkError: err && !err.response,
      });
      setStatus('ready');
    }
  }, [session, sessionId]);

  const statusMessages = {
    connecting: 'Connecting to your wallet...',
    paying: 'Waiting for you to approve the transfer in your wallet...',
    'waiting-confirmation': 'Transaction submitted — waiting for confirmation on-chain...',
    reporting: 'Payment confirmed — finishing up...',
    'stripe-redirecting': 'Starting Stripe Checkout...',
    'stripe-checking': 'Confirming your card payment...',
  };

  const title = session
    ? `${formatProductName(session.product)} payment`
    : 'e3d.ai payment';

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <Typography className={classes.title}>{title}</Typography>

        {status === 'loading' && (
          <div className={classes.status}>
            <CircularProgress size={16} style={{ color: '#ffffff' }} />
            <span>Loading payment details...</span>
          </div>
        )}

        {session && (status === 'ready' || status === 'connecting' || status === 'paying' || status === 'waiting-confirmation' || status === 'reporting') && (
          <>
            <div className={classes.row}><span>Wallet</span><span>{session.wallet}</span></div>
            <div className={classes.row}><span>Amount</span><span>{session.quote.payment.requiredAmount} {session.quote.payment.token}</span></div>
            <div className={classes.row}><span>Network</span><span>{session.quote.payment.network}</span></div>
            <div className={classes.row}><span>Credits</span><span>{session.quote.requestedIssuedCredits}</span></div>
          </>
        )}

        {session && status === 'ready' && (
          <>
            <Typography className={classes.sectionTitle}>Pay with wallet</Typography>
            <Button
              className={classes.button}
              variant="contained"
              color="primary"
              onClick={handlePay}
            >
              Connect wallet & pay
            </Button>

            {stripeAvailable && (
              <>
                <Typography className={classes.sectionTitle}>Pay with card</Typography>
                <div className={classes.optionList}>
                  {stripePacks.map((pack) => (
                    <Button
                      key={pack.id}
                      className={classes.packButton}
                      variant="outlined"
                      color="primary"
                      onClick={() => handleCardCheckout(pack.id)}
                    >
                      <span>{pack.name} · {Number(pack.credits || 0).toLocaleString()} credits</span>
                      <span>{formatPackPrice(pack)}</span>
                    </Button>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {statusMessages[status] && (
          <div className={classes.status}>
            <CircularProgress size={16} style={{ color: '#ffffff' }} />
            <span>{statusMessages[status]}</span>
          </div>
        )}

        {status === 'done' && (
          <div className={classes.successBox}>
            <div>{completionMessage || 'Payment received — you can close this tab and return to your terminal.'}</div>
            {creditKey ? <div>Credit key: {creditKey}</div> : null}
          </div>
        )}

        {error && <Typography className={classes.error}>{error}</Typography>}

        {stripeCancelled && status === 'ready' && (
          <Typography className={classes.status}>
            Card checkout was cancelled. You can choose another payment option below.
          </Typography>
        )}

        {debugInfo && (
          <pre className={classes.debug}>{JSON.stringify(debugInfo, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}
